import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { MenuOutlined, MoonFilled } from "@ant-design/icons";
import { Drawer, Flex, Space, theme } from "antd";
import { createStyles } from "antd-style";

import { Button } from "@av/components/atoms/Button";

import type { NavItem } from "./types";

const useStyles = createStyles(({ token, css }) => ({
  navLink: css`
    display: flex;
    align-items: center;
    width: 100%;
    height: 44px;
    padding-left: ${token.paddingSM}px;
    text-align: left;
    border: none;
    justify-content: flex-start;
    background: transparent;
    color: ${token.colorText};
    font-weight: ${token.fontWeightStrong};
    text-decoration: none;
    border-radius: ${token.borderRadius}px;
    transition: all ${token.motionDurationMid};

    &:hover {
      background: ${token.colorBgTextHover};
      color: ${token.colorPrimaryTextHover};
    }

    &.active {
      background: ${token.colorPrimaryBgHover};
      color: ${token.colorLink};
      font-weight: ${token.fontWeightStrong};
    }
  `,
}));

interface ResponsiveNavProps {
  navItems: NavItem[];
  onDarkModeToggle: () => void;
}

function ResponsiveNav({ navItems, onDarkModeToggle }: ResponsiveNavProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { token } = theme.useToken();
  const { styles } = useStyles();

  const showDrawer = (): void => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Button
        testIdPrefix="responsive-nav-show-drawer"
        type="text"
        icon={<MenuOutlined />}
        onClick={showDrawer}
        style={{
          width: "40px",
          height: "40px",
        }}
        aria-label="Open navigation menu"
      />
      <Drawer
        placement="left"
        closable={false}
        onClose={onClose}
        open={open}
        styles={{
          body: {
            display: "flex",
            flexDirection: "column",
            backgroundColor: token.colorPrimaryBg,
            padding: "16px",
            height: "100%",
          },
        }}
      >
        <div style={{ flexGrow: 1 }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            {/* TODO: The child nav items are not implemented, maybe we need to shift to menu items */}
            {navItems.map((item) => (
              <NavLink
                data-testid={"responsive-nav-" + item.key}
                to={item.path}
                key={item.key}
                onClick={() => setOpen(false)}
                className={styles.navLink}
              >
                {({ isActive }) => (
                  <Flex gap={14}>
                    {item.icon(isActive)} {item.label}
                  </Flex>
                )}
              </NavLink>
            ))}
          </Space>
        </div>
        <Button
          testIdPrefix="floating-nav-dark-mode"
          icon={<MoonFilled />}
          onClick={onDarkModeToggle}
          type="primary"
        >
          {t("common.components.organisms_nav_toggleDarkMode")}
        </Button>
      </Drawer>
    </>
  );
}

export { ResponsiveNav };
